/**
 * VIB-1197: Lås reverserte bilag + originalene deres for fordeling.
 *
 * PowerOffice lager automatisk en negativ ReversalVoucher + et nytt korrigert
 * bilag når et bokført bilag endres. Begge, OG originalen som ble reversert,
 * skal fortsatt VISES i bilagslista (sporbarhet), men kan IKKE fordeles på
 * budsjettlinjer (de inngår ikke i kostnadsgrunnlaget — å fordele dem ville
 * dobbelttelle).
 *
 * Et bilag er LÅST for fordeling hvis:
 *   1) det er av typen `ReversalVoucher` (en reversering er alltid et
 *      korreksjonsartefakt — fanger også de eldre der PO ikke ga oss
 *      `reversedVoucherId`, jf. bilag 9052), ELLER
 *   2) det har `po_reversed_voucher_id` (reversering med kjent kobling), ELLER
 *   3) dets `po_voucher_id` refereres av en reverserings `po_reversed_voucher_id`
 *      (dvs. det er en original som er blitt reversert).
 *
 * Koblingen til originalen er den pålitelige PO-referansen (`reversedVoucherId`),
 * ikke en beløp/leverandør-heuristikk.
 */

/** Minste subsett av et bilag som lås-avledningen trenger. */
export interface VoucherLockInput {
  id: string;
  po_voucher_id: string;
  po_reversed_voucher_id: string | null;
  voucher_type: string | null;
}

/**
 * Avled settet av bilags-`id`-er som er låst for fordeling, gitt hele
 * bilagslista for et prosjekt.
 */
export function deriveLockedVoucherIds(
  vouchers: VoucherLockInput[],
): Set<string> {
  // PO-voucher-id-er som er blitt reversert (målene for en reversering).
  const reversedOriginalPoIds = new Set<string>();
  for (const v of vouchers) {
    if (v.po_reversed_voucher_id) {
      reversedOriginalPoIds.add(v.po_reversed_voucher_id);
    }
  }

  const locked = new Set<string>();
  for (const v of vouchers) {
    // 1) Bilaget ER en reversering (per type — fanger også eldre uten ref).
    if (v.voucher_type === "ReversalVoucher" || v.po_reversed_voucher_id) {
      locked.add(v.id);
      continue;
    }
    // 2) Bilaget er en original som er blitt reversert.
    if (reversedOriginalPoIds.has(v.po_voucher_id)) {
      locked.add(v.id);
    }
  }
  return locked;
}

/** Praktisk per-bilag-predikat når settet allerede er avledet. */
export function isVoucherLockedForAllocation(
  voucherId: string,
  lockedIds: Set<string>,
): boolean {
  return lockedIds.has(voucherId);
}
